import {
  AlertTriangle,
  CheckCircle,
  ExternalLink,
  Loader2,
  RefreshCw,
  Settings,
  XCircle,
} from 'lucide-react'
import React, { useState } from 'react'
import {
  useOAuthConfigQuery,
  useOAuthStatusQuery,
  useRefreshOAuthTokenMutation,
  useResetOAuthMutation,
  useTestOAuthConnectionMutation,
} from '../hooks'
import { Spinner } from '@/components/Spinner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { formatDate } from '@/utils/timeZoneUtils'

const OAuth2Dashboard: React.FC = () => {
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date())

  // Fetch OAuth2 status
  const {
    data: status,
    isLoading: statusLoading,
    error: statusError,
    refetch: refetchStatus,
    invalidate: invalidateStatus,
  } = useOAuthStatusQuery()

  // Fetch OAuth2 configuration
  const {
    data: config,
    isLoading: configLoading,
    invalidate: invalidateConfig,
  } = useOAuthConfigQuery()

  // Reset OAuth2 configuration
  const resetMutation = useResetOAuthMutation(() => {
    invalidateStatus()
    invalidateConfig()
    setLastRefresh(new Date())
  })

  // Test connection mutation
  const testMutation = useTestOAuthConnectionMutation(() => {
    invalidateStatus()
    setLastRefresh(new Date())
  })

  // Refresh token mutation
  const refreshMutation = useRefreshOAuthTokenMutation(() => {
    invalidateStatus()
    setLastRefresh(new Date())
  })

  const getStatusColor = () => {
    if (!status?.connectionTest) return 'destructive'
    return status.connectionTest.success
      ? 'bg-green-200'
      : status.reauthRequired
        ? 'bg-yellow-200'
        : 'bg-red-200'
  }

  const getStatusText = () => {
    if (!status?.connectionTest) return 'Unknown'
    return status.connectionTest.success
      ? 'Healthy'
      : status.reauthRequired
        ? 'Needs Reauth'
        : 'Error'
  }

  const getStatusIcon = () => {
    if (!status?.connectionTest) return <XCircle className="h-4 w-4" />
    return status.connectionTest.success ? (
      <CheckCircle className="h-4 w-4 text-green-600" />
    ) : status.reauthRequired ? (
      <AlertTriangle className="h-4 w-4 text-yellow-600" />
    ) : (
      <XCircle className="h-4 w-4 text-red-600" />
    )
  }

  const handleAuthenticate = () => {
    if (status?.authUrl) {
      window.open(status.authUrl, '_blank', 'width=600,height=700')
      setTimeout(() => {
        refetchStatus()
      }, 5000)
    }
  }

  const isLoading = statusLoading || configLoading
  const isActionLoading =
    resetMutation.isPending ||
    testMutation.isPending ||
    refreshMutation.isPending

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    )
  }

  if (statusError) {
    return (
      <div className="flex flex-col justify-center h-full space-y-4 px-4">
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center text-red-700">
              <AlertTriangle className="h-4 w-4 mr-2" />
              Cannot Load Dashboard
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-red-600 wrap-break-words">
              Could not load the email dashboard: {statusError.message}
            </p>
          </CardContent>
        </Card>
        <Button onClick={() => refetchStatus()} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4 px-4 sm:px-6 lg:px-8 py-4">
      {/* Header */}
      <div className="shrink-0 space-y-2">
        <div className="flex items-start sm:items-center justify-between gap-4">
          <div className="flex-1 min-w-0 w-full">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight wrap-break-words">
              Email Setup Dashboard
            </h1>
            <p className="text-sm text-muted-foreground hidden md:block">
              Manage email permissions for sending donation receipts and
              notifications
            </p>
          </div>
          <div className="flex items-center space-x-2 shrink-0">
            <Badge
              variant="secondary"
              className={`flex items-center space-x-1 ${getStatusColor()}`}
            >
              {getStatusIcon()}
              <span className="hidden sm:inline">{getStatusText()}</span>
            </Badge>
            <Button
              onClick={() => refetchStatus()}
              variant="outline"
              size="sm"
              disabled={isActionLoading}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {/* Status Overview */}
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-medium">
                Configuration
              </CardTitle>
              <Settings className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {status?.hasClientCredentials ? 'Ready' : 'Setup Needed'}
              </div>
              <p className="text-xs text-muted-foreground">
                Email system setup
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-medium">
                Email System
              </CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {status?.hasIntegration ? 'Connected' : 'Disconnected'}
              </div>
              <p className="text-xs text-muted-foreground">
                Email system connection
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-medium">Permissions</CardTitle>
              <RefreshCw className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {status?.hasRefreshToken && status.hasAccessToken
                  ? 'Valid'
                  : 'Invalid'}
              </div>
              <p className="text-xs text-muted-foreground">
                Email sending permissions
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-medium">Service</CardTitle>
              {getStatusIcon()}
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{getStatusText()}</div>
              <p className="text-xs text-muted-foreground">
                Email service status
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Recommendations */}
        {status?.recommendations && status.recommendations.length > 0 && (
          <Card className="border-yellow-200 bg-yellow-50">
            <CardHeader>
              <CardTitle className="flex items-center text-yellow-700">
                <AlertTriangle className="h-4 w-4 mr-2" />
                Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-1 text-yellow-800">
                {status.recommendations.map((rec, index) => (
                  <li key={index} className="text-sm wrap-break-words">
                    {rec}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>
              Manage email permissions and fix email sending issues
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-1 w-full">
                  <h4 className="font-medium text-sm">Give Email Permission</h4>
                  <p className="text-xs text-muted-foreground">
                    {status?.reauthRequired
                      ? 'Your email permission has expired. Click here to give the app permission to send emails again.'
                      : 'Give the app permission to send donation receipts and emails through your Gmail account.'}
                  </p>
                </div>
                <Button
                  onClick={handleAuthenticate}
                  variant={status?.reauthRequired ? 'default' : 'outline'}
                  disabled={isActionLoading || !status?.authUrl}
                  className="w-full sm:w-44 shrink-0"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {status?.reauthRequired ? 'Reauthenticate' : 'Authenticate'}
                </Button>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-1 w-full">
                  <h4 className="font-medium text-sm">Test Email System</h4>
                  <p className="text-xs text-muted-foreground">
                    Check if the app can successfully send emails. This helps
                    troubleshoot any email delivery issues.
                  </p>
                </div>
                <Button
                  onClick={() => testMutation.mutate()}
                  variant="outline"
                  disabled={isActionLoading}
                  className="w-full sm:w-44 shrink-0"
                >
                  {testMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Test Connection
                </Button>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-1 w-full">
                  <h4 className="font-medium text-sm">Refresh Permissions</h4>
                  <p className="text-xs text-muted-foreground">
                    Refresh your email permissions if they've expired. Try this
                    if emails aren't being sent.
                  </p>
                </div>
                <Button
                  onClick={() => refreshMutation.mutate()}
                  variant="outline"
                  disabled={isActionLoading || !status?.hasRefreshToken}
                  className="w-full sm:w-44 shrink-0"
                >
                  {refreshMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Refresh Tokens
                </Button>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-1 w-full">
                  <h4 className="font-medium text-sm">Reset Permissions</h4>
                  <p className="text-xs text-muted-foreground">
                    Remove all email permissions and start fresh. Use this{' '}
                    <span className="font-bold">ONLY</span> if you're having
                    persistent email problems.
                  </p>
                </div>
                <Button
                  onClick={() => resetMutation.mutate()}
                  variant="destructive"
                  disabled={isActionLoading}
                  className="w-full sm:w-44 shrink-0"
                >
                  {resetMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <XCircle className="h-4 w-4 mr-2" />
                  )}
                  Reset Config
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Technical Details */}
        <div className="grid gap-3 grid-cols-1 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Email Setup Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                <div className="flex justify-between items-center gap-2">
                  <span className="text-sm font-medium">Email Setup:</span>
                  <Badge
                    variant={
                      config?.hasClientCredentials ? 'default' : 'destructive'
                    }
                  >
                    {config?.hasClientCredentials ? 'Configured' : 'Missing'}
                  </Badge>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                  <span className="text-sm font-medium">Email Settings:</span>
                  <code className="text-xs bg-muted px-2 py-1 rounded break-all">
                    {config?.redirectUri || 'Not set'}
                  </code>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                  <span className="text-sm font-medium">Last Updated:</span>
                  <span className="text-xs text-muted-foreground">
                    {status?.systemInfo?.lastUpdated
                      ? formatDate(
                          status.systemInfo.lastUpdated,
                          'DD/MM/YYYY hh:mm:ss A',
                        )
                      : 'Never'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Email Problems</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {status?.lastError ? (
                <div className="space-y-2">
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-sm font-medium">Recent Problem:</span>
                    <Badge variant="destructive">Error</Badge>
                  </div>
                  <div className="text-xs bg-destructive/10 p-2 rounded border wrap-break-words">
                    {status.lastError}
                  </div>
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-1">
                    <span className="text-sm font-medium">
                      When it happened:
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {status.lastAuthErrorAt
                        ? formatDate(
                            status.lastAuthErrorAt,
                            'DD/MM/YYYY hh:mm:ss A',
                          )
                        : 'Unknown'}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-4 text-muted-foreground">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Everything is working well!
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <div className="shrink-0 text-center text-xs text-muted-foreground py-2 border-t">
        Last refreshed: {formatDate(lastRefresh, 'DD/MM/YYYY hh:mm:ss A')}
        <span className="hidden sm:inline"> • Auto-refresh: 5min</span>
      </div>
    </div>
  )
}

export default OAuth2Dashboard
